import React, { useEffect, useMemo, useState } from "react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { Share2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const CIRCLES = [
  { key: "feed",  label: "Public feed" },
  { key: "close", label: "Close friends" },
  { key: "kind",  label: "Kind souls" },
];

const AGE_GROUPS = ["20-30", "30-40", "40-50", "50-60", "60+"];

const GOAL_TAG_MAP = {
  skin:      "skin care",
  skincare:  "skin care",
  movement:  "fitness",
  fitness:   "fitness",
  strength:  "fitness",
  cardio:    "fitness",
  sleep:     "sleep",
  rest:      "sleep",
  nutrition: "nutrition",
  hormones:  "hormones",
  breath:    "mindset",
  mindset:   "mindset",
  emotional: "emotional wellbeing",
  circadian: "sleep",
  reflection:"emotional wellbeing",
  "nervous system": "mindset",
};

function inferGoalTag(ritual) {
  const cat = (ritual?.category || "").toLowerCase().trim();
  if (GOAL_TAG_MAP[cat]) return [GOAL_TAG_MAP[cat]];
  return [];
}

/**
 * ShareRitualDialog — opens from a Share button on any ritual card. Prefills
 * a soft caption around the ritual title + description, lets the member
 * tweak it, picks a circle + age group, and posts to the community as a
 * regular check-in. The result appears in the feed with the right goal tag
 * so it lands next to other ritual-driven posts.
 */
export default function ShareRitualDialog({ ritual, open, onClose, onShared }) {
  const initial = useMemo(() => {
    if (!ritual) return "";
    const bits = [];
    bits.push(`Today's ritual: ${ritual.title}.`);
    if (ritual.description) bits.push(ritual.description);
    if (ritual.steps?.length) {
      bits.push(`\nHow I do it:\n${ritual.steps.map((s, i) => `${i + 1}. ${s}`).join("\n")}`);
    }
    return bits.join("\n");
  }, [ritual]);

  const [content, setContent] = useState(initial);
  const [circle, setCircle]   = useState("feed");
  const [ageGroup, setAgeGroup] = useState("");
  const [isWin, setIsWin] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reset when the source ritual changes.
  useEffect(() => { setContent(initial); }, [initial]);
  useEffect(() => {
    if (!open) {
      setCircle("feed"); setAgeGroup(""); setIsWin(false);
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = content.trim();
    if (!text) return;
    setBusy(true);
    try {
      await endpoints.checkin({
        content: text,
        circle,
        age_group: ageGroup || undefined,
        goal_tags: inferGoalTag(ritual),
        is_win: !!isWin,
      });
      toast.success("Ritual shared.");
      onShared?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not share.");
    }
    setBusy(false);
  };

  if (!ritual) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid={`share-ritual-dialog-${ritual.slug}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
            <Share2 size={16} className="text-[hsl(var(--terracotta))]" />
            Share this ritual
          </DialogTitle>
          <DialogDescription>
            Post to the circle so others learn from your practice. Edit the wording — it&apos;s your voice.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Caption</label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={7}
              maxLength={2000}
              data-testid={`share-ritual-content-${ritual.slug}`}
              className="mt-1 leading-relaxed"
            />
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 tabular-nums">
              {content.trim().length} / 2000
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Circle</label>
              <select
                value={circle}
                onChange={(e) => setCircle(e.target.value)}
                data-testid={`share-ritual-circle-${ritual.slug}`}
                className="mt-1 w-full bg-transparent border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--clay))]/50"
              >
                {CIRCLES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Age group (optional)</label>
              <select
                value={ageGroup}
                onChange={(e) => setAgeGroup(e.target.value)}
                data-testid={`share-ritual-agegroup-${ritual.slug}`}
                className="mt-1 w-full bg-transparent border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--clay))]/50"
              >
                <option value="">—</option>
                {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          <label
            className="inline-flex items-center gap-2 text-sm cursor-pointer select-none"
            data-testid={`share-ritual-iswin-${ritual.slug}`}
          >
            <input
              type="checkbox"
              checked={isWin}
              onChange={(e) => setIsWin(e.target.checked)}
              className="accent-[hsl(var(--terracotta))]"
            />
            <span>Mark as a <span className="italic text-[hsl(var(--terracotta))]">win</span></span>
          </label>

          <DialogFooter className="pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose?.()}
              className="rounded-full"
              data-testid={`share-ritual-cancel-${ritual.slug}`}
            >
              Not now
            </Button>
            <Button
              type="submit"
              disabled={!content.trim() || busy}
              data-testid={`share-ritual-submit-${ritual.slug}`}
              className="rounded-full bg-[hsl(var(--terracotta))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--terracotta))]/90"
            >
              {busy ? "Sharing…" : "Share"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
